import { create } from 'zustand'

import { updateMarketShopRestaurantOrderStatus } from '@/api/market-shop-restaurant/update-market-shop-restaurant-order-status'
import { MarketShopOrderModel, MarketShopOrderStatus, NullableType } from '@/models'

export type MarketShopRestaurantState = {
  orders: Record<MarketShopOrderStatus, Array<MarketShopOrderModel>>
  newOrdersSet: Set<MarketShopOrderModel['id']>
  updatingOrders: Set<MarketShopOrderModel['id']>
  startDragPosition: { x: number; y: number }
  draggedOrder: NullableType<MarketShopOrderModel>
  dropTargetColumn: NullableType<MarketShopOrderStatus>
  findOrder: (
    id: MarketShopOrderModel['id'],
    inStatus?: MarketShopOrderStatus,
  ) => NullableType<MarketShopOrderModel>
  changeOrderStatus: (order: MarketShopOrderModel, newStatus: MarketShopOrderStatus) => void
  setDraggedOrder: (
    draggedOrder: NullableType<MarketShopOrderModel>,
    startDragPosition: MarketShopRestaurantState['startDragPosition'],
  ) => void
  setDropTargetColumn: (column: NullableType<MarketShopOrderStatus>) => void
  initializeOrders: (orders: Array<MarketShopOrderModel>) => void
  registerNewOrder: (order: MarketShopOrderModel) => void
  removeOrderFromNewSet: (order: MarketShopOrderModel) => void
  setOrderUpdating: (order: MarketShopOrderModel, isUpdating: boolean) => void
  updateOrderStatus: (
    order: MarketShopOrderModel,
    newStatus: MarketShopOrderStatus,
  ) => Promise<void>
}

const useMarketShopRestaurantStore = create<MarketShopRestaurantState>((set, getState) => ({
  changeOrderStatus: (order, newStatus) => {
    set((state) => {
      const currentStatus = order.status
      const updatedOldStatusColumn = state.orders[currentStatus].filter((o) => o.id !== order.id)

      const hasRemoved = updatedOldStatusColumn.length < state.orders[currentStatus].length
      if (!hasRemoved) return state

      state.newOrdersSet.delete(order.id)

      return {
        newOrdersSet: state.newOrdersSet,
        orders: {
          ...state.orders,
          [currentStatus]: updatedOldStatusColumn,
          [newStatus]: state.orders[newStatus]
            .concat({ ...order, status: newStatus })
            .sort((a, b) => b.createdAt - a.createdAt),
        },
      }
    })
  },
  draggedOrder: null,
  dropTargetColumn: null,
  findOrder: (id, inStatus) => {
    const orders = getState().orders

    if (inStatus) {
      return orders[inStatus].find((o) => o.id === id)
    }

    for (const currentStatus in orders) {
      const order = orders[currentStatus as MarketShopOrderStatus].find((o) => o.id === id)
      if (order) return order
    }

    return undefined
  },
  initializeOrders: (orders) => {
    return set((state) => {
      const ordersState = { ...state.orders }

      // Clear every status
      Object.keys(ordersState).forEach((key) => {
        ordersState[key as MarketShopOrderStatus] = []
      })

      // Assign orders to their status
      orders.forEach((order) => {
        const status = order.status
        if (status in ordersState) {
          ordersState[status].push(order)
        }
      })

      // Sort orders within each status by date
      Object.keys(ordersState).forEach((key) => {
        const status = key as MarketShopOrderStatus
        ordersState[status] = ordersState[status].sort((a, b) => b.createdAt - a.createdAt)
      })

      return { orders: ordersState }
    })
  },
  newOrdersSet: new Set(),
  orders: {
    awaiting_pickup: [],
    completed: [],
    declined: [],
    in_progress: [],
    new: [],
  },
  registerNewOrder: (order) => {
    return set((state) => {
      return {
        newOrdersSet: state.newOrdersSet.add(order.id),
        orders: {
          ...state.orders,
          [order.status]: state.orders[order.status]
            .concat(order)
            .sort((a, b) => b.createdAt - a.createdAt),
        },
      }
    })
  },
  removeOrderFromNewSet: (order) => {
    return set((state) => {
      state.newOrdersSet.delete(order.id)
      return { newOrdersSet: state.newOrdersSet }
    })
  },
  setDraggedOrder: (draggedOrder, startDragPosition) => {
    return set((state) => {
      if (draggedOrder) state.newOrdersSet.delete(draggedOrder.id)
      return { draggedOrder, newOrdersSet: state.newOrdersSet, startDragPosition }
    })
  },
  setDropTargetColumn: (column) => {
    return set((state) => {
      if (state.dropTargetColumn === column) return state
      return { dropTargetColumn: column }
    })
  },
  setOrderUpdating: (order, isUpdating) => {
    return set((state) => {
      if (isUpdating) {
        state.updatingOrders.add(order.id)
      } else {
        state.updatingOrders.delete(order.id)
      }
      return { updatingOrders: new Set(state.updatingOrders) }
    })
  },
  startDragPosition: { x: 0, y: 0 },
  // NOTE, we do have an optimistic approach for order status change. Thus, to make it convenient,
  // it's beneficial to handle api call within store action here...
  updateOrderStatus: async (order, newStatus) => {
    const { changeOrderStatus, setOrderUpdating } = getState()
    const oldStatus = order.status

    setOrderUpdating(order, true)
    changeOrderStatus(order, newStatus)

    try {
      await updateMarketShopRestaurantOrderStatus({ id: order.id, status: newStatus })
    } catch {
      changeOrderStatus({ ...order, status: newStatus }, oldStatus)
    } finally {
      setOrderUpdating(order, false)
    }
  },
  updatingOrders: new Set(),
}))

export { useMarketShopRestaurantStore }
